// WishEventTimeline — vertical timeline component for the W35 §2
// audit log (per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md).
//
// Renders the wish's recent actions (created / blocks / status /
// complete-with-diary / progress / chat / reminder / timeline / phase)
// with actor avatar, localized action label, and relative time.
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~7KB).

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActivityStreamCard } from "@/components/activity/ActivityStreamCard";
import { ErrorState } from "@/components/primitives/ErrorState";
import { listWishEvents, type WishEventOut } from "@/lib/api/wishlist";
import { toApiDate } from "@/lib/utils/date";

export interface WishEventTimelineProps {
  wishId: string;
  className?: string;
}

const ACTION_LABELS: Record<string, string> = {
  created: "建立了心願",
  updated: "更新了心願",
  deleted: "刪除了心願",
  status_changed: "狀態變更",
  block_created: "新增了區塊",
  block_updated: "更新了區塊",
  block_deleted: "刪除了區塊",
  block_reordered: "重新排序了區塊",
  completed_with_diary: "完成並記錄了日記",
  progress_logged: "記錄了進度",
  progress_log_deleted: "刪除了進度記錄",
  reminder_created: "新增了提醒",
  reminder_updated: "更新了提醒",
  reminder_deleted: "刪除了提醒",
  chat_message_created: "新增了留言",
  chat_message_edited: "編輯了留言",
  chat_message_deleted: "刪除了留言",
  timeline_created: "新增了里程碑",
  timeline_updated: "更新了里程碑",
  timeline_deleted: "刪除了里程碑",
  timeline_completed: "完成了里程碑",
  phase_created: "新增了階段",
  phase_updated: "更新了階段",
  phase_deleted: "刪除了階段",
  phase_completed: "完成了階段",
};

function relativeTime(iso: string): string {
  // `toApiDate` — the API sends naive UTC and JS reads a zone-less string as
  // local, so every one of these was off by the UTC offset. See
  // `lib/utils/date.ts::toApiDate`.
  const then = toApiDate(iso);
  const diffSec = Math.round((Date.now() - then.getTime()) / 1000);
  if (diffSec < 60) return "剛剛";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} 分鐘前`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} 小時前`;
  if (diffSec < 86400 * 7) return `${Math.round(diffSec / 86400)} 天前`;
  return then.toLocaleDateString("zh-TW");
}

function ActionLabel({ action }: { action: string }) {
  return <>{ACTION_LABELS[action] ?? action}</>;
}

export function WishEventTimeline({
  wishId,
  className,
}: WishEventTimelineProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const query = useQuery({
    queryKey: ["wishes", "detail", wishId, "events"],
    queryFn: () => listWishEvents(wishId, { limit: expanded ? 50 : 10 }),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  const events: WishEventOut[] = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    // W52.1 D-5 — the shell is now shared with the anniversary section and the
    // dashboard. What stays local is the **strategy**: this one re-queries with
    // a bigger `limit`, so expanding is a server round trip, while the other
    // two slice rows they already have. The shell does not care, which is the
    // entire reason for extracting it.
    <ActivityStreamCard
      title="操作記錄"
      total={total}
      visibleCount={events.length}
      expanded={expanded}
      onToggleExpanded={() => {
        setExpanded((v) => !v);
        void queryClient.invalidateQueries({
          queryKey: ["wishes", "detail", wishId, "events"],
        });
      }}
      testId="wish-event-timeline"
      className={className}
    >
      {query.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : query.isError ? (
        // W54.0 B1 — a failed read is not 「還沒有任何操作記錄」. The empty
        // state says nothing ever happened; the error state says we could not
        // find out. Only the first one is worth acting on.
        <ErrorState onRetry={() => void query.refetch()} />
      ) : events.length === 0 ? (
        <p className="text-meta text-ink-3">還沒有任何操作記錄。</p>
      ) : (
        <ol className="flex flex-col gap-s-2">
          {events.map((ev) => (
            <li
              key={ev.id}
              data-component="WishEventTimelineRow"
              data-testid={`wish-event-row-${ev.id}`}
              data-action={ev.action}
              className="flex items-start gap-2 text-body-sm"
            >
              {ev.user ? (
                <span
                  className="shrink-0 w-7 h-7 rounded-full bg-paper-deep text-ink-2 flex items-center justify-center text-meta font-semibold"
                  aria-hidden
                >
                  {ev.user.display_name.slice(0, 1).toUpperCase()}
                </span>
              ) : (
                <span
                  className="shrink-0 w-7 h-7 rounded-full bg-paper-deep text-ink-3 flex items-center justify-center text-meta"
                  aria-hidden
                >
                  ?
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-ink-1">
                  {ev.user ? (
                    <span className="font-semibold">{ev.user.display_name}</span>
                  ) : (
                    <span className="text-ink-3">系統</span>
                  )}
                  {" "}
                  <ActionLabel action={ev.action} />
                </p>
                <p className="text-meta text-ink-3 tabular-nums">
                  {relativeTime(ev.created_at)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </ActivityStreamCard>
  );
}